// src/auth/Login.jsx
import React, { useState, useEffect } from 'react';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '../supabaseClient';
import { Loader, AlertCircle, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [isSignUp, setIsSignUp] = useState(false);
  const [inviteMode, setInviteMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [signUpForm, setSignUpForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    breweryName: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');

  // Detect invite context on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isInvite = params.get('invite') === '1';
    const inviteEmail = sessionStorage.getItem('pendingInviteEmail');
    const inviteName = sessionStorage.getItem('pendingInviteName');
    const inviteToken = sessionStorage.getItem('pendingInviteToken');
    
    // Enable invite mode if we have invite token in sessionStorage (most reliable)
    // OR if URL has invite=1 parameter
    if (inviteToken || isInvite) {
      setInviteMode(true);
      setIsSignUp(true);
      // Set flag immediately to prevent onboarding modal
      sessionStorage.setItem('justAcceptedInvite', 'true');
      if (inviteEmail) {
        setSignUpForm(f => ({ ...f, email: inviteEmail }));
      }
      if (inviteName) {
        setSignUpForm(f => ({ ...f, name: inviteName }));
      }
    }

    // Listen for auth state changes to handle invite acceptance after sign in
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session?.user) {
        // Check if user has pending invite to accept
        const pendingToken = sessionStorage.getItem('pendingInviteToken');
        if (pendingToken) {
          console.log('🎫 User signed in with pending invite, attempting to accept...');
          try {
            const { default: BreweryService } = await import('../services/breweryService');
            const result = await BreweryService.acceptInvite(pendingToken);
            
            if (result.success) {
              console.log('✅ Invite accepted successfully after sign in');
              // Clear invite session data
              sessionStorage.removeItem('pendingInviteToken');
              sessionStorage.removeItem('pendingInviteEmail');
              sessionStorage.removeItem('pendingInviteName');
              sessionStorage.removeItem('pendingInviteRole');
              sessionStorage.removeItem('pendingInviteBreweryName');
              
              // Set flag to prevent onboarding modal
              sessionStorage.setItem('justAcceptedInvite', 'true');
              
              // Redirect to dashboard
              window.location.href = '/';
            } else {
              console.error('❌ Failed to accept invite after sign in:', result.error);
              // Don't show error here - user is already signed in, they can try again later
            }
          } catch (err) {
            console.error('❌ Error accepting invite after sign in:', err);
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignUpChange = (e) => {
    const { name, value } = e.target;
    setSignUpForm(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // Validation
    if (!signUpForm.email || !signUpForm.password || !signUpForm.confirmPassword || !signUpForm.name || (!inviteMode && !signUpForm.breweryName)) {
      setError('All fields are required');
      setLoading(false);
      return;
    }

    if (signUpForm.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (signUpForm.password !== signUpForm.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (signUpForm.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      setLoading(false);
      return;
    }

    try {
      // Sign up user
      const userMeta = { name: signUpForm.name };
      if (!inviteMode && signUpForm.breweryName && signUpForm.breweryName.trim() !== '') {
        userMeta.brewery_name = signUpForm.breweryName.trim();
        // Also set default_owner so the server-side trigger persists the default owner name
        userMeta.default_owner = signUpForm.breweryName.trim();
      }
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: signUpForm.email,
        password: signUpForm.password,
        options: {
          data: userMeta
        }
      });

      if (signUpError) {
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      if (!data.user) {
        setError('Failed to create account');
        setLoading(false);
        return;
      }

      if (inviteMode) {
        // For invite mode, don't try to accept immediately - user needs to confirm email first
        // Store invite data for after they sign in
        setSuccess('Your Account has been created! Confirm your Account via email to get started!');
        setSignUpForm({ email: '', password: '', confirmPassword: '', name: '', breweryName: '' });
        
        // Don't clear invite session data - keep it for after login
        // The invite will be accepted when they sign in after email confirmation
        setTimeout(() => setIsSignUp(false), 3000);
        return; // Don't continue with normal brewery creation
      }

      // Brewery creation for normal signups is handled server-side via a trigger on auth.users
      // If the user provided a brewery name it will be created automatically when the user row is inserted.

      console.log('🔍 SignUp response:', data);
      setSuccess('Account created! We sent a confirmation email — check your inbox (and spam). If you did not receive it, click "Resend confirmation email" below.');
      setSignUpForm({ email: '', password: '', confirmPassword: '', name: '', breweryName: '' });
      setAwaitingConfirmationEmail(true);
      // Don't auto-close signup modal so user can click resend if needed
      // setTimeout(() => setIsSignUp(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const [awaitingConfirmationEmail, setAwaitingConfirmationEmail] = React.useState(false);

  const handleResendConfirmation = async (email) => {
    try {
      setLoading(true);
      // Use magic link as a fallback to get the user logged in / verify email
      const { data: otpData, error: otpError } = await supabase.auth.signInWithOtp({ email });
      if (otpError) {
        console.error('Error resending confirmation / sending magic link:', otpError);
        setError('Failed to resend confirmation. Please contact support or check SMTP settings.');
      } else {
        console.log('🔍 Resend/magic-link response:', otpData);
        setSuccess('Resend initiated. Check your inbox for a sign-in link (or spam).');
        setAwaitingConfirmationEmail(false);
      }
    } catch (err) {
      console.error('Unexpected error resending confirmation:', err);
      setError('Unexpected error resending confirmation. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo & Branding */}
        <div className="text-center mb-8">
          <img src="/kegops-logo.png" alt="KegOps" className="h-16 mx-auto mb-4" />
          {awaitingConfirmationEmail && (
            <div className="mt-4 text-sm text-left bg-yellow-50 border border-yellow-200 rounded p-3 text-yellow-800">
              <p className="mb-2">We sent a confirmation email. Didn't receive it?</p>
              <div className="flex gap-2">
                <button
                  className="btn btn-sm bg-yellow-500 hover:bg-yellow-600 text-white"
                  onClick={() => handleResendConfirmation(signUpForm.email)}
                >
                  Resend confirmation email
                </button>
                <button
                  className="btn btn-sm bg-transparent border border-gray-200 text-gray-700"
                  onClick={() => { setAwaitingConfirmationEmail(false); setIsSignUp(false); }}
                >
                  Dismiss
                </button>
              </div>
            </div>
          )}

          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">KegOps</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">Manage • Monitor • Maintain</p>
        </div>

        {/* Auth Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-4">
          {isSignUp ? (
            // Sign Up Form
            <>
              {inviteMode ? (
                <div className="text-center mb-4">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Create Your Account</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Enter your name and set a password to join your team.
                  </p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-4">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Welcome to KegOps!</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Let's get your first brewery set up. Start with our free plan and upgrade anytime.
                    </p>
                  </div>
                  {/* Free Plan Benefits */}
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4 text-left">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-200 mb-2">
                      🎉 Free Plan Includes:
                    </p>
                    <ul className="text-sm text-amber-800 dark:text-amber-300 space-y-1">
                      <li>✓ Up to 2 kegs</li>
                      <li>✓ 1 location</li>
                      <li>✓ 2 sublocations</li>
                      <li>✓ Up to 2 team members</li>
                    </ul>
                  </div>
                </>
              )}

              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 mb-4 flex gap-2">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded p-3 mb-4">
                  <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
                </div>
              )}


              <form onSubmit={handleSignUpSubmit} className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={signUpForm.email}
                    onChange={handleSignUpChange}
                    placeholder="you@example.com"
                    disabled={loading || success || (inviteMode && signUpForm.email)}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  {inviteMode && signUpForm.email && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Email cannot be changed for invite acceptance
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Your Name</label>
                  <input
                    type="text"
                    name="name"
                    value={signUpForm.name}
                    onChange={handleSignUpChange}
                    placeholder="John Doe"
                    disabled={loading || success}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                  />
                </div>

                {!inviteMode && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Brewery Name</label>
                    <input
                      type="text"
                      name="breweryName"
                      value={signUpForm.breweryName}
                      onChange={handleSignUpChange}
                      placeholder="My Brewery"
                      disabled={loading || success}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={signUpForm.password}
                      onChange={handleSignUpChange}
                      placeholder="••••••••"
                      disabled={loading || success}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading || success}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm Password</label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={signUpForm.confirmPassword}
                      onChange={handleSignUpChange}
                      placeholder="••••••••"
                      disabled={loading || success}
                      className="w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-2 pr-10 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      disabled={loading || success}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading || success}
                  className="w-full px-4 py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 text-white rounded font-semibold transition flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Creating Account...
                    </>
                  ) : (
                    'Create Account'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(false);
                    setError('');
                    setSuccess('');
                  }}
                  disabled={loading}
                  className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded font-semibold transition disabled:opacity-50"
                >
                  Back to Sign In
                </button>
              </form>
            </>
          ) : (
            // Sign In Form
            <>
              <div className="text-center mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">Sign In</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Welcome back</p>
              </div>
              <Auth
                supabaseClient={supabase}
                appearance={{ theme: ThemeSupa }}
                theme="dark"
                providers={["google", "github"]}
                view="sign_in"
                showLinks={false}
              />
            </>
          )}
        </div>

        {/* Sign Up Link - Below Card */}
        {!isSignUp && (
          <div className="text-center mb-4">
            <button
              onClick={() => setIsSignUp(true)}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-amber-600 dark:hover:text-amber-400 font-medium"
            >
              Don't have an account? <span className="text-amber-600 dark:text-amber-400 font-semibold">Create one</span>
            </button>
          </div>
        )}

        {/* Footer Text */}
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
          By continuing you agree to our Terms and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
